import React, { memo, useEffect } from 'react';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { isAfter, addDays, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { stateToTzMap } from '@ourbranch/state-to-tz-map';

import { useToast } from 'core/components/toast';
import { Label } from 'core/components/label';
import Field from 'core/components/form/form.v2';
import DatePicker from 'core/components/date-picker';
import withDatePicker from 'core/components/with-date-picker';
import { CognitoPermissionGroups, PermissionRequireTypes } from 'core/helpers/cognito-permission-groups';
import useStyles from './segment-form.styles';

const SegmentForm = memo(
  ({
    onChange,
    startDate: from,
    minDate,
    maxDate,
    error,
    editingFirstSegment,
    isEditingSegment,
    policy,
    renewalChangesBlocked
  }) => {
    const classes = useStyles();
    const { setFieldValue, values } = useFormikContext();
    const toast = useToast();

    // "Underwriting Changes for Renewal" only applies to Create New Change: the backend rejects it on every
    // Edit Selected Change, including edits to an existing renewal segment, and non-team-leaders can't
    // uncheck the box themselves.
    useEffect(() => {
      if (isEditingSegment && values.underwritingChanges) {
        setFieldValue('underwritingChanges', false);
      }
    }, [isEditingSegment, values.underwritingChanges, setFieldValue]);

    //checks UW box if user does not have permission to do so if this is a renewal segment due to current UW backend rules
    const handleUWCheckbox = (from) => {
      onChange(from);
      // #36833: a blocked term never gets a renewal change, so a post-term date must not auto-check it;
      // the backend refuses the change outright.
      if (isEditingSegment || renewalChangesBlocked) {
        return;
      }
      const segmentStartDate = from.value;
      const endDate = utcToZonedTime(new Date(policy.endDate), stateToTzMap[policy.state]);
      if (isAfter(segmentStartDate, endDate)) {
        setFieldValue('underwritingChanges', true);
      } else {
        setFieldValue('underwritingChanges', false);
      }
    };

    const startDate = values.underwritingChanges ? addDays(maxDate, 1) : from;

    return (
      <Grid container alignItems="flex-end" className={classes.container}>
        <Grid item>
          <DatePicker
            name="startDate"
            onChange={handleUWCheckbox}
            value={startDate}
            error={!!error}
            helperText={error}
            mode="light"
            disabled={editingFirstSegment}
            disableFuture={false}
            label="Start date"
            className={classes.datePicker}
            minDate={minDate}
            maxDate={maxDate}
            fast={false}
          />
        </Grid>
        <Grid item>
          {/* #36833: no renewal changes on a migrated policy where the brand blocks them (CFM): a mid-term
              IRPM must not ride into the renewal, which restores the legacy carrier's adjustment. */}
          {renewalChangesBlocked && (
            <Label type="infoLabel" component="div" className={classes.renewalChangesBlocked}>
              Renewal changes are not available on a migrated policy; make them on the renewal policy once it generates.
            </Label>
          )}
          {!renewalChangesBlocked && (
            <Field
              type="checkbox"
              id="underwritingChanges"
              name="underwritingChanges"
              mode="light"
              label="Underwriting Changes for Renewal"
              fast={false}
              disabled={isEditingSegment}
              onChange={(event) => {
                if (event.target.checked) {
                  const renewalStartDate = addDays(maxDate, 1);
                  setFieldValue('startDate', renewalStartDate);
                  onChange({ value: renewalStartDate });
                  toast.notify({
                    type: 'success',
                    message: `Updated start date for this change to ${format(renewalStartDate, 'MM/dd/yyyy')} since you selected underwriting changes for renewal.`
                  });
                } else {
                  setFieldValue('startDate', from);
                  onChange({ value: from });
                }
              }}
              permissions={{
                edit: {
                  groups: [CognitoPermissionGroups.isTeamLeader],
                  type: PermissionRequireTypes.AtLeastOne
                }
              }}
            />
          )}
        </Grid>
      </Grid>
    );
  }
);

SegmentForm.propTypes = {
  onChange: PropTypes.func.isRequired,
  startDate: PropTypes.object.isRequired,
  minDate: PropTypes.object.isRequired,
  maxDate: PropTypes.object.isRequired,
  error: PropTypes.string,
  isEditingSegment: PropTypes.bool,
  renewalChangesBlocked: PropTypes.bool
};

SegmentForm.defaultProps = {
  error: null,
  isEditingSegment: false,
  renewalChangesBlocked: false
};

export default withDatePicker(SegmentForm);
